/*
 * Marken-Bausteine für den E-Mail-Designer.
 * Alle Styles sind explizite MJML-Attribute: grapesjs-mjml wendet mj-class/mj-attributes im Canvas nicht an.
 */
const COLORS = {
  blue: '#113e7c',
  red: '#E31E24',
  redText: '#e3000a',
  grey: '#f0f0f0',
  white: '#ffffff'
}

const icon = path => `<svg viewBox="0 0 24 24"><path fill="currentColor" d="${path}"/></svg>`

/**
 * Klappt selbstschließende MJML-Tags auf: "<mj-image … />" → "<mj-image …></mj-image>".
 * Der HTML-Parser (DOMParser) kennt keine selbstschließenden Custom-Elements und würde alle folgenden
 * Geschwister als Kinder des Bildes einsortieren; MJML lässt solche Kinder beim Rendern weg.
 */
export function expandSelfClosingMjmlTags(mjml) {
  return (mjml || '').replace(/<(mj-[a-z0-9-]+)(\s[^<>]*?)?\s*\/>/gi, '<$1$2></$1>')
}

const ICONS = {
  header: 'M3 4h18v4H3V4m0 6h18v10H3V10Z',
  hero: 'M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 16H3V5h18v14M5 17h14l-4.5-6-3.5 4.5-2.5-3L5 17Z',
  headline: 'M3 4h4v7h10V4h4v16h-4v-7H7v7H3V4Z',
  text: 'M3 5h18v2H3V5m0 4h12v2H3V9m0 4h18v2H3v-2m0 4h12v2H3v-2Z',
  button: 'M4 7h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2m2 4v2h12v-2H6Z',
  cta: 'M2 4h20v16H2V4m2 2v12h16V6H4m3 3h10v2H7V9m2 4h6v2H9v-2Z',
  columns: 'M3 4h8v16H3V4m10 0h8v16h-8V4Z',
  contact: 'M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4Z',
  qr: 'M3 3h8v8H3V3m2 2v4h4V5H5m8-2h8v8h-8V3m2 2v4h4V5h-4M3 13h8v8H3v-8m2 2v4h4v-4H5m12-2h2v2h-2v-2m-4 0h2v2h-2v-2m2 2h2v2h-2v-2m2 2h2v2h-2v-2m-4 0h2v2h-2v-2m2 2h2v2h-2v-2m2 2h2v2h-2v-2Z',
  divider: 'M3 11h18v2H3v-2Z',
  spacer: 'M7 2h10v2H7V2m0 18h10v2H7v-2m4-14h2v12h-2V6Z',
  footer: 'M3 4h18v10H3V4m0 12h18v4H3v-4Z'
}

export function createBrandBlocks({ baseUrl, category, targets, audience }) {
  const img = name => `${baseUrl}/images/${name}`
  const firstTarget = targets && targets[0]
  // Logisches Ziel statt URL: wird beim Rendern je Umgebung aufgelöst (siehe Feld „Ziel“ am Button).
  const buttonHref = firstTarget ? firstTarget.placeholder : '{{link:Login}}'
  const buttonText = firstTarget ? firstTarget.buttonText : 'Zur App'

  const button = `<mj-button href="${buttonHref}" background-color="${COLORS.red}" color="${COLORS.white}" border-radius="0" font-size="16px" padding="20px 0">${buttonText}</mj-button>`

  const blocks = [
    {
      id: 'sf-header',
      label: 'Kopfzeile mit Logo',
      media: icon(ICONS.header),
      content: `<mj-section background-color="${COLORS.blue}" padding="10px 20px">
        <mj-column vertical-align="middle">
          <mj-text align="left" color="${COLORS.white}" font-size="22px" padding="10px 0">{{fairName}}</mj-text>
        </mj-column>
        <mj-column vertical-align="middle">
          <mj-image align="right" alt="SAGASSER Unternehmensgruppe" src="${img('email-logo.png')}" width="240px" padding="0" />
        </mj-column>
      </mj-section>`
    },
    {
      id: 'sf-hero',
      label: 'Hero-Bild',
      media: icon(ICONS.hero),
      content: `<mj-section background-color="${COLORS.white}" padding="0">
        <mj-column>
          <mj-image alt="Hausmesse" src="${img('email-header-2027.png')}" padding="0" />
        </mj-column>
      </mj-section>`
    },
    {
      id: 'sf-headline',
      label: 'Überschrift',
      media: icon(ICONS.headline),
      content: `<mj-text color="${COLORS.redText}" font-size="18px" font-weight="bold" padding="10px 25px">Überschrift</mj-text>`
    },
    {
      id: 'sf-text',
      label: 'Textabsatz',
      media: icon(ICONS.text),
      content: `<mj-text color="#000000" font-size="14px" line-height="1.5" padding="10px 25px">{{salutation}},<br/><br/>hier Text einfügen …</mj-text>`
    },
    {
      id: 'sf-button',
      label: 'Button',
      media: icon(ICONS.button),
      content: button
    },
    {
      id: 'sf-cta',
      label: 'Call to Action',
      media: icon(ICONS.cta),
      content: `<mj-section background-color="${COLORS.blue}" padding="20px 0">
        <mj-column>
          <mj-text align="center" color="${COLORS.white}" font-size="20px" font-weight="bold">Jetzt anmelden</mj-text>
          <mj-text align="center" color="${COLORS.white}" font-size="14px">Kurzer Text, warum sich ein Klick lohnt.</mj-text>
          ${button}
        </mj-column>
      </mj-section>`
    },
    {
      id: 'sf-image-text',
      label: 'Bild + Text',
      media: icon(ICONS.columns),
      content: `<mj-section background-color="${COLORS.white}">
        <mj-column width="40%">
          <mj-image alt="Service-Team" src="${img('email-service_team_alternative3.png')}" />
        </mj-column>
        <mj-column width="60%" vertical-align="middle">
          <mj-text font-size="14px" line-height="1.5">Text neben dem Bild, z. B. Hinweis auf das Service-Team.</mj-text>
        </mj-column>
      </mj-section>`
    },
    {
      id: 'sf-contact',
      label: 'Ansprechpartner',
      media: icon(ICONS.contact),
      content: `<mj-section background-color="${COLORS.white}">
        <mj-column width="33%">
          <mj-image align="center" alt="Ansprechpartner" src="${img('email-ponsel.png')}" width="120px" />
          <mj-text align="center" font-size="13px"><strong>Zuständigkeit</strong></mj-text>
          <mj-text align="center" font-size="14px"><strong>Vorname Nachname</strong></mj-text>
          <mj-text align="center" font-size="13px"><a href="mailto:vorname.nachname@sagasser.de" style="color:${COLORS.redText};text-decoration:none">E-Mail senden</a><br/><br/>+49 9561 0000</mj-text>
        </mj-column>
        <mj-column width="33%">
          <mj-image align="center" alt="Ansprechpartner" src="${img('email-weigel.png')}" width="120px" />
          <mj-text align="center" font-size="13px"><strong>Zuständigkeit</strong></mj-text>
          <mj-text align="center" font-size="14px"><strong>Vorname Nachname</strong></mj-text>
          <mj-text align="center" font-size="13px"><a href="mailto:vorname.nachname@sagasser.de" style="color:${COLORS.redText};text-decoration:none">E-Mail senden</a><br/><br/>+49 9561 0000</mj-text>
        </mj-column>
        <mj-column width="33%">
          <mj-image align="center" alt="Ansprechpartner" src="${img('email-bonnke.png')}" width="120px" />
          <mj-text align="center" font-size="13px"><strong>Zuständigkeit</strong></mj-text>
          <mj-text align="center" font-size="14px"><strong>Vorname Nachname</strong></mj-text>
          <mj-text align="center" font-size="13px"><a href="mailto:vorname.nachname@sagasser.de" style="color:${COLORS.redText};text-decoration:none">E-Mail senden</a><br/><br/>+49 9561 0000</mj-text>
        </mj-column>
      </mj-section>`
    },
    {
      id: 'sf-divider',
      label: 'Trennlinie',
      media: icon(ICONS.divider),
      content: `<mj-divider border-color="${COLORS.grey}" border-width="2px" padding="10px 25px" />`
    },
    {
      id: 'sf-spacer',
      label: 'Abstand',
      media: icon(ICONS.spacer),
      content: '<mj-spacer height="20px" />'
    },
    {
      id: 'sf-footer',
      label: 'Fußzeile',
      media: icon(ICONS.footer),
      content: `<mj-section background-color="${COLORS.blue}" padding="10px 0">
        <mj-column width="50%">
          <mj-text color="${COLORS.white}" font-size="13px"><a href="https://www.sagasser.de/" style="color:${COLORS.white};text-decoration:none"><strong>SAGASSER Unternehmensgruppe</strong></a></mj-text>
        </mj-column>
        <mj-column width="50%">
          <mj-text align="right" color="${COLORS.white}" font-size="13px"><a href="https://www.sagasser.de/kontakt" style="color:${COLORS.white};text-decoration:none">Kontakt</a> &nbsp; <a href="https://www.sagasser.de/impressum" style="color:${COLORS.white};text-decoration:none">Impressum</a> &nbsp; <a href="https://www.sagasser.de/datenschutz" style="color:${COLORS.white};text-decoration:none">Datenschutz</a></mj-text>
        </mj-column>
      </mj-section>`
    }
  ]

  if (audience !== 'Supplier') {
    blocks.splice(8, 0, {
      id: 'sf-qr',
      label: 'QR-Code (Einlass)',
      media: icon(ICONS.qr),
      content: `<mj-section background-color="${COLORS.white}">
        <mj-column>
          <mj-text color="${COLORS.redText}" font-size="16px" font-weight="bold">Ihr persönlicher QR-Code</mj-text>
          <mj-text font-size="14px">Bitte zeigen Sie diesen Code am Einlass vor.</mj-text>
          <mj-image align="center" alt="QR-Code" fluid-on-mobile="true" src="{{qrCodeUrl}}" width="200px" />
        </mj-column>
      </mj-section>`
    })
  }

  return blocks.map(b => ({ ...b, category, select: true }))
}
